import React, { useEffect, useRef, useState } from 'react';
import { Map, TileLayer, Marker, Popup , Tooltip, Circle, Polyline, CircleMarker} from 'react-leaflet';
import { Link } from 'react-router-dom'; 
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import Alerts from './Alerts';
import ServerSettings from './ServerSettings';
import DetectorsGeneral from './DetectorsGeneral';
import Detector from './Detector';
import Schedules from './Schedules';
import RouteLibrary from './RouteLibrary';

// 1. IMPORTAMOS TU ICONO DE CALAVERA
import calaveraIcon from '../assets/icona-calavera.svg';
import calaveraIconGray from '../assets/icona-calavera-gris.svg';
import calaveraIconBlue from '../assets/icona-calavera-blau.svg';
import homeIconSVG from '../assets/icona-calavera-casa.svg';
import pilotIconSVG from '../assets/icona-calavera-comandament2.svg';
import calaveraIconRed from '../assets/icona-calavera-red.svg';

const tacticalSkullIcon = new L.Icon({
    iconUrl: calaveraIcon,
    iconSize: [110, 110],
    iconAnchor: [45, 75],
    tooltipAnchor: [10, -70], 
    popupAnchor: [1.5, 155],    
});

const tacticalSkullIconOffline = new L.Icon({
    iconUrl: calaveraIconGray,
    iconSize: [110, 110],
    iconAnchor: [45, 75],
    tooltipAnchor: [10, -70], 
    popupAnchor: [1.5, 155],
});

const tacticalSkullIconArmed = new L.Icon({
    iconUrl: calaveraIconBlue,
    iconSize: [110, 110],
    iconAnchor: [45, 75],
    tooltipAnchor: [10, -70], 
    popupAnchor: [1.5, 155],
});

const tacticalSkullIconRed = new L.Icon({
    iconUrl: calaveraIconRed,
    iconSize: [110, 110],
    iconAnchor: [45, 75],
    tooltipAnchor: [10, -70], 
    popupAnchor: [1.5, 155],
});

const homeIcon = new L.Icon({
    iconUrl: homeIconSVG,
    iconSize: [70, 70],
    iconAnchor: [35, 65],
    className: ''
});

const pilotIcon = new L.Icon({
    iconUrl: pilotIconSVG,
    iconSize: [70, 70],
    iconAnchor: [35, 65],
    className: ''
});

const dragHandleIcon = new L.divIcon({
    className: 'custom-drag-anchor',
    html: `<div style="
        width: 24px; 
        height: 24px; 
        background: rgba(0, 188, 212, 0.2); 
        border: 2px solid #00bcd4; 
        border-radius: 50%; 
        display: flex; 
        justify-content: center; 
        align-items: center; 
        box-shadow: 0 0 15px rgba(0, 188, 212, 0.8); 
        cursor: grab;">
            <div style="width: 8px; height: 8px; background: #00bcd4; border-radius: 50%;"></div>
    </div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12]
});

// ==========================================
// COMPONENTE SONAR 
// ==========================================
const SonarWaves = ({ position, isSpoofing }) => {
    const wave1Ref = useRef(null);
    const wave2Ref = useRef(null);
    const wave3Ref = useRef(null);
    
    const positionRef = useRef(position);
    useEffect(() => { positionRef.current = position; }, [position]);

    useEffect(() => {
        if (!isSpoofing || !position) return;

        const MAX_RADIUS_VISIBLE = 1500; 
        const GROWTH_SPEED = 5;          

        const waves = [
            wave1Ref.current?.leafletElement,
            wave2Ref.current?.leafletElement,
            wave3Ref.current?.leafletElement
        ].filter(Boolean);

        if (waves.length === 0) return;

        let animationFrameId;
        let radii = [0, -500, -1000];

        const animateSonar = () => {
            const visualLatOffset = 0.0005; 
            const visualLonOffset = 0.0004; 
            const visualCenterPos = [positionRef.current.lat + visualLatOffset, positionRef.current.lon + visualLonOffset];

            for (let i = 0; i < waves.length; i++) {
                radii[i] += GROWTH_SPEED;
                if (radii[i] >= MAX_RADIUS_VISIBLE) radii[i] -= MAX_RADIUS_VISIBLE; 

                let r = radii[i];

                if (r > 0) {
                    waves[i].setRadius(r);
                    waves[i].setLatLng(visualCenterPos);

                    let opacity = 0.4 * (1 - (r / MAX_RADIUS_VISIBLE));
                    if (r < 200) opacity = 0.4 * (r / 200); 

                    opacity = Math.max(0, opacity);
                    waves[i].setStyle({ opacity: opacity, fillOpacity: opacity / 2 });
                } else {
                    waves[i].setRadius(0);
                    waves[i].setStyle({ opacity: 0, fillOpacity: 0 });
                }
            }
            animationFrameId = requestAnimationFrame(animateSonar);
        };

        animateSonar(); 

        return () => {
            if (animationFrameId) cancelAnimationFrame(animationFrameId);
        };
    }, [isSpoofing]); 

    if (!isSpoofing || !position) return null;

    const circleOpts = { color: '#000000', weight: 2, fillColor: '#000000', opacity: 0, fillOpacity: 0, interactive: false };

    return (
        <React.Fragment>
            <Circle ref={wave1Ref} center={[position.lat, position.lon]} radius={0} {...circleOpts} />
            <Circle ref={wave2Ref} center={[position.lat, position.lon]} radius={0} {...circleOpts} />
            <Circle ref={wave3Ref} center={[position.lat, position.lon]} radius={0} {...circleOpts} />
        </React.Fragment>
    );
};

// ==========================================
// MARCADOR DEL DRON (Con UI Dinámica y Calavera Roja)
// ==========================================
const DeviceMarker = ({ device, onActionSuccess, onSelect, registerMarker, onSpotSelect, onSpotDeselect, spotConfig, onSpotLock, onArmedChange, globalCancelSignal }) => {
    const [selectedMode, setSelectedMode] = useState(device.operationMode ? device.operationMode.toUpperCase() : "CUSTOM");
    const [isCommanding, setIsCommanding] = useState(false);
    const [isPopupOpen, setIsPopupOpen] = useState(false); 
    const isActuallyOnline = device.online;
    const [showScheduler, setShowScheduler] = useState(false);
    const [scheduledTime, setScheduledTime] = useState("");
    const [scheduledDuration, setScheduledDuration] = useState(""); 
    const [isArmed, setIsArmed] = useState(false);
    
    // Estado para la calavera roja
    const [spoofCenter, setSpoofCenter] = useState(null); 
    
    // Referencia para forzar la apertura del popup al soltar la calavera
    const redMarkerRef = useRef(null);
    const prevSpoofingRef = useRef(device.spoofing);

    useEffect(() => {
        // Si recibimos una señal de cancelación externa y es para nuestro equipo...
        if (globalCancelSignal && globalCancelSignal.ident === device.ident) {
            setIsArmed(false);
            if (onArmedChange) onArmedChange(device.ident, false);
            setScheduledTime("");
            setScheduledDuration("");
            setSpoofCenter(null); // Borra la calavera roja
            setSelectedMode("CUSTOM"); // Resetea a CUSTOM
            onSpotDeselect(device.ident); // Borra la ruta de Spot si la hubiera
        }
    }, [globalCancelSignal]);

    useEffect(() => {
        setIsCommanding(false);
        setIsArmed(false);
        if (onArmedChange) onArmedChange(device.ident, false);

        if (prevSpoofingRef.current === true && device.spoofing === false) {
            setSpoofCenter(null);         // 1. Destruimos la calavera roja
            setSelectedMode("CUSTOM");    // 2. Reseteamos al modo por defecto
            onSpotDeselect(device.ident); // 3. Borramos cualquier ruta dibujada en el mapa
        }

        prevSpoofingRef.current = device.spoofing;
    }, [device.spoofing]);

    useEffect(() => {
        if (device.operationMode && device.operationMode.toUpperCase() !== selectedMode) {
            setSelectedMode(device.operationMode.toUpperCase());
            if (device.operationMode.toUpperCase() !== "SPOT") {
                onSpotDeselect(device.ident);
            }
        }
    }, [device.operationMode]);

    const getMinMaxDates = () => {
        const now = new Date();
        const tzOffset = now.getTimezoneOffset() * 60000;
        const minDate = new Date(now.getTime() - tzOffset).toISOString().slice(0, 16);
        const maxDate = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000 - tzOffset).toISOString().slice(0, 16);
        return { minDate, maxDate };
    };
    const { minDate, maxDate } = getMinMaxDates();

    const handleModeChange = (newMode) => {
        if (device.spoofing) return; 
        setSelectedMode(newMode);
        if (newMode === "SPOT") {
            onSpotSelect(device.ident, [device.location.lat, device.location.lon]);
        } else {
            onSpotDeselect(device.ident);
        }
    };

    const handleCancelSchedule = async () => {
        try {
            const token = localStorage.getItem('token');
            const userEmail = localStorage.getItem('email') || "admin@admin.com";
            const response = await fetch(`${process.env.REACT_APP_API_URL}/action`, {
                method: 'POST',
                headers: { 'x-access-token': token, 'Content-Type': 'application/json' },
                body: JSON.stringify({ ident: device.ident, type: "action", payload: { target: "spoofing", action: "cancel_schedule", user: userEmail } })
            });

            if (response.ok) {
                setIsArmed(false);
                if (onArmedChange) onArmedChange(device.ident, false);
                setScheduledTime("");
                setScheduledDuration("");
                setSelectedMode("CUSTOM");
                onSpotDeselect(device.ident); 
                if (onActionSuccess) await onActionSuccess(); 
            }
        } catch (error) { console.error(error); }
    };

    const handleMainAction = async () => {
        if (isCommanding) return;
        setIsCommanding(true);
        const isScheduling = scheduledTime ? true : false;
        
        try {
            const token = localStorage.getItem('token');
            const userEmail = localStorage.getItem('email') || "admin@admin.com";
            const newAction = device.spoofing ? "disable" : "enable";

            // 1. Configuramos los parámetros base
            let finalParams = { tacticalProfile: selectedMode };
            
            // 2. Si es modo SPOT, inyectamos la ruta
            if (selectedMode === "SPOT" && spotConfig && newAction === "enable") {
                finalParams = { ...finalParams, spotPath: spotConfig.waypoints, spotHome: spotConfig.home, spotPilot: spotConfig.pilot };
            }
            
            // 👇 3. MAGIA: Si hay calavera roja, inyectamos sus coordenadas exactas en el payload
            if (spoofCenter && newAction === "enable") {
                finalParams.spoofingLatitude = spoofCenter[0].toString();
                finalParams.spoofingLongitude = spoofCenter[1].toString();
            }

            const response = await fetch(`${process.env.REACT_APP_API_URL}/action`, {
                method: 'POST',
                headers: { 'x-access-token': token, 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    ident: device.ident, 
                    type: "action", 
                    payload: { 
                        target: "spoofing", 
                        action: newAction, 
                        params: finalParams, // Aquí viajan las coordenadas rojas si existen
                        user: userEmail, 
                        scheduledTime: scheduledTime || null, 
                        scheduledDuration: scheduledDuration ? parseInt(scheduledDuration, 10) : null 
                    } 
                })
            });

            if (response.ok && onActionSuccess) {
                await onActionSuccess(); 
                if (isScheduling) {
                    setShowScheduler(false);
                    setScheduledTime("");
                    setScheduledDuration("");
                    setIsArmed(true);
                    if (onArmedChange) onArmedChange(device.ident, true);
                    if (selectedMode === "SPOT" && onSpotLock) onSpotLock(device.ident); 
                }
            } else if (!response.ok) { 
                setIsCommanding(false); 
            }
        } catch (error) {
            console.error("Error al enviar comando:", error);
            setIsCommanding(false);
        } finally {
            setTimeout(() => { setIsCommanding(false); }, isScheduling ? 1000 : 5000);
        }
    };

    if (!device.location || !device.location.lat || !device.location.lon) return null;

    const modWifi = device.modules?.find(m => m.type === 0) || {};
    const modRid = device.modules?.find(m => m.type === 1) || {};
    const modOcu = device.modules?.find(m => m.type === 2) || {};
    const rfOnline = modOcu.isOnline;
    const rfSpoofing = modOcu.isSpoofing && modOcu.iqInjectionEnabled && device.operationMode !== "SPOT";

    const getModClass = (mod) => mod.isSpoofing ? "mod-label spoofing" : (mod.isOnline ? "mod-label online" : "mod-label");
    const getRfClass = () => rfSpoofing ? "mod-label spoofing" : (rfOnline ? "mod-label online" : "mod-label");

    // =========================================================================
    // 👇 INTERFAZ TÁCTICA EMPAQUETADA (Viajará de la negra a la roja) 👇
    // =========================================================================
    const tacticalUI = (
        <div className="tactical-ui-container">
            {isArmed ? (
                <div className="tactical-armed-wrapper">
                    <button className="btn-pill btn-armed-cancel" onClick={handleCancelSchedule}>
                        DELETE SCHEDULE
                    </button>
                </div>
            ) : (
                <React.Fragment>
                    <div className="top-config-row">
                        <div 
                            className={`segmented-control ${showScheduler ? 'shrunk' : ''}`}
                            onClick={() => { if (showScheduler) setShowScheduler(false); }}
                        >
                            {["FOLLOW", "SPOT", "CUSTOM", "SWARM", "ATTACK"].map((mode) => {
                                if (showScheduler && selectedMode !== mode) return null;
                                return (
                                    <React.Fragment key={mode}>
                                        <input
                                            type="radio"
                                            id={`mode-${mode.toLowerCase()}-${device.ident}`}
                                            value={mode}
                                            checked={selectedMode === mode}
                                            onChange={(e) => handleModeChange(e.target.value)}
                                            disabled={device.spoofing || isCommanding}
                                        />
                                        <label htmlFor={`mode-${mode.toLowerCase()}-${device.ident}`}>{mode}</label>
                                    </React.Fragment>
                                );
                            })}
                        </div>

                        {!device.spoofing && (
                            <div className={`scheduler-wrapper ${showScheduler ? 'expanded' : ''} ${scheduledTime ? 'has-schedule' : ''}`}>
                                <div 
                                    className="scheduler-icon-btn"
                                    onClick={() => {
                                        if (showScheduler) {
                                            setScheduledTime(""); 
                                            setScheduledDuration(""); 
                                        }
                                        setShowScheduler(!showScheduler);
                                    }}
                                    title="Time on Target"
                                >
                                    <i className="fa-solid fa-stopwatch"></i>
                                </div>
                                
                                <div className="tactical-inline-inputs">
                                    <input 
                                        type="datetime-local" 
                                        className="tactical-inline-date"
                                        min={minDate}
                                        max={maxDate}
                                        value={scheduledTime}
                                        onChange={(e) => setScheduledTime(e.target.value)}
                                    />
                                    <div className="duration-wrapper">
                                        <input 
                                            type="number" 
                                            className="tactical-inline-duration"
                                            placeholder="∞"
                                            min="1"
                                            title="Duration (Minutes)"
                                            value={scheduledDuration}
                                            onChange={(e) => setScheduledDuration(e.target.value)}
                                        />
                                        <span className="duration-label">MIN</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="action-row">
                        <button
                            className={`btn-pill ${device.spoofing ? 'active' : ''} ${isCommanding ? 'cooldown-state' : ''}`}
                            onClick={() => { handleMainAction(); }}
                            disabled={isCommanding}
                        >
                            {device.spoofing ? "STOP" : (scheduledTime ? "SCHEDULE" : "START")}
                        </button>
                    </div>
                </React.Fragment>
            )}
        </div>
    );

    const activeSonarLocation = spoofCenter ? { lat: spoofCenter[0], lon: spoofCenter[1] } : device.location;

    return (
        <React.Fragment>
            <SonarWaves position={activeSonarLocation} isSpoofing={isActuallyOnline && device.spoofing} />
            
            {/* --- CALAVERA NEGRA (EQUIPO REAL) --- */}
            <Marker
                ref={(ref) => registerMarker && registerMarker(device.ident, ref)}
                position={[device.location.lat, device.location.lon]}
                icon={!isActuallyOnline ? tacticalSkullIconOffline : (isArmed ? tacticalSkullIconArmed : tacticalSkullIcon)}
                draggable={isActuallyOnline && !device.spoofing && !isArmed} 
                onDragstart={() => setIsPopupOpen(false)} 
                onDragend={(e) => {
                    const pos = e.target.getLatLng();
                    setSpoofCenter([pos.lat, pos.lng]); 
                    e.target.setLatLng([device.location.lat, device.location.lon]);
                    
                    setTimeout(() => {
                        if (redMarkerRef.current && redMarkerRef.current.leafletElement) {
                            redMarkerRef.current.leafletElement.openPopup();
                        }
                    }, 150);
                }}
                onClick={() => {
                    if (isActuallyOnline && !spoofCenter) {
                        onSelect(device.location.lat, device.location.lon);
                    }
                }}
                onPopupOpen={() => !spoofCenter && setIsPopupOpen(true)}   
                onPopupClose={() => !spoofCenter && setIsPopupOpen(false)} 
            >
                {(isPopupOpen || device.spoofing) && (
                    <Tooltip permanent className="tactical-floating-info" direction="top" offset={[0, -25]}>
                        <div className={`tactical-sn-pill ${!isActuallyOnline ? 'offline' : (isArmed ? 'armed' : '')}`}>{device.alias || device.ident}</div>
                        {isActuallyOnline && (
                            <div className="tactical-modules-pill">
                                <span className={getModClass(modWifi)}>WIFI</span>
                                <span className={getModClass(modRid)}>RID</span>
                                <span className={getModClass(modOcu)}>OCU</span>
                                <span className={getRfClass()}>RF</span>
                            </div>
                        )}
                    </Tooltip>
                )}

                {!spoofCenter && (
                    <Popup className={`tactical-floating-ui ${!isActuallyOnline ? 'offline-hidden-popup' : ''}`} closeButton={false} autoPan={false}>
                        {isActuallyOnline && tacticalUI}
                    </Popup>
                )}
            </Marker>

            {/* 👇 NUEVO: LÍNEA DE ENLACE TÁCTICO (TETHER) 👇 */}
            {spoofCenter && (
                <Polyline 
                    positions={[
                        [device.location.lat, device.location.lon], // Origen: Calavera Negra
                        spoofCenter                                 // Destino: Calavera Roja
                    ]}
                    color="#ff3333"     // Rojo táctico
                    weight={2}          // Grosor fino
                    opacity={0.5}       // Semitransparente para no molestar
                    dashArray="8, 8"    // Línea punteada
                    lineJoin="round"
                />
            )}

            {/* --- CALAVERA ROJA (CENTRO DE SPOOFING MANUAL) --- */}
            {spoofCenter && (
                <Marker
                    ref={redMarkerRef}
                    position={spoofCenter}
                    icon={tacticalSkullIconRed}
                    draggable={!device.spoofing && !isArmed}
                    zIndexOffset={1000} 
                    onDragstart={() => setIsPopupOpen(false)}
                    
                    // 👇 NUEVO: onDrag para actualizar la línea EN TIEMPO REAL al mover el ratón
                    onDrag={(e) => {
                        const pos = e.target.getLatLng();
                        setSpoofCenter([pos.lat, pos.lng]);
                    }}
                    
                    onDragend={(e) => {
                        const pos = e.target.getLatLng();
                        setSpoofCenter([pos.lat, pos.lng]);
                        setTimeout(() => {
                            if (redMarkerRef.current && redMarkerRef.current.leafletElement) {
                                redMarkerRef.current.leafletElement.openPopup();
                            }
                        }, 150);
                    }}
                    onDblclick={() => {
                        if (!device.spoofing && !isArmed) {
                            setSpoofCenter(null);
                            setIsPopupOpen(false);
                        }
                    }}
                    onClick={() => isActuallyOnline && onSelect(spoofCenter[0], spoofCenter[1])}
                    onPopupOpen={() => setIsPopupOpen(true)}
                    onPopupClose={() => setIsPopupOpen(false)}
                >
                    <Tooltip direction="top" offset={[0, -60]} opacity={0.8} permanent={!isPopupOpen}>
                        <div style={{ textAlign: 'center', fontSize: '10px', color: '#ff3333', fontWeight: 'bold', fontFamily: 'monospace' }}>
                            SPOOFING CENTER<br/>(Dbl-click to reset)
                        </div>
                    </Tooltip>

                    <Popup className={`tactical-floating-ui ${!isActuallyOnline ? 'offline-hidden-popup' : ''}`} closeButton={false} autoPan={false}>
                        {isActuallyOnline && tacticalUI}
                    </Popup>
                </Marker>
            )}
        </React.Fragment>
    );
};



// --- COMPONENTE PRINCIPAL DEL MAPA ---
// --- MAPA PRINCIPAL ---
const MapView = () => {
    const mapRef = useRef(null);
    const [devices, setDevices] = useState([]);
    const [showAlertsPanel, setShowAlertsPanel] = useState(false);
    const [showSettingsPanel, setShowSettingsPanel] = useState(false);
    const [showDirectoryPanel, setShowDirectoryPanel] = useState(false);
    const [showDetectorDetailPanel, setShowDetectorDetailPanel] = useState(false);
    const [selectedDetectorId, setSelectedDetectorId] = useState(null);
    const markerRefs = useRef({});
    const totalDevices = devices.length;
    const onlineDevices = devices.filter(d => d.online).length;
    const spoofingDevices = devices.filter(d => d.online && d.spoofing).length;
    const [showSchedulesPanel, setShowSchedulesPanel] = useState(false);
    const [hasActiveSchedules, setHasActiveSchedules] = useState(false);
    const [showRouteLibraryPanel, setShowRouteLibraryPanel] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const isAnySidePanelOpen = showAlertsPanel || showSettingsPanel || showDirectoryPanel || showDetectorDetailPanel || showSchedulesPanel || showRouteLibraryPanel;

    // --- ESTADOS PARA EL MODO SPOT Y BLOQUEOS ---
    const [spotConfigs, setSpotConfigs] = useState({}); 
    const [activeDrawingDevice, setActiveDrawingDevice] = useState(null); 
    const [armedDevices, setArmedDevices] = useState({}); // Memoria de los equipos programados

    const [cancelSignal, setCancelSignal] = useState(null);

    // 👇 NUEVO: Función que aplica la ruta de forma Relativa (Cian) o Absoluta (Naranja)
    const handleApplyRoute = (routeData, isAbsolute = false) => {
        if (activeDrawingDevice) {
            const targetDevice = devices.find(d => d.ident === activeDrawingDevice);
            if (!targetDevice) return;

            let finalWaypoints = routeData.waypoints;
            let finalHome = routeData.home;
            let finalPilot = routeData.pilot;
            let finalAnchor = routeData.waypoints[0]; // El ancla por defecto se pondrá en el primer waypoint

            // Si NO es absoluto (es decir, es relativo al dron), calculamos el desplazamiento
            if (!isAbsolute) {
                const origStart = routeData.waypoints[0];
                const targetStart = [targetDevice.location.lat, targetDevice.location.lon];
                const dLat = targetStart[0] - origStart[0];
                const dLng = targetStart[1] - origStart[1];

                finalWaypoints = routeData.waypoints.map(wp => [wp[0] + dLat, wp[1] + dLng]);
                finalHome = routeData.home ? [routeData.home[0] + dLat, routeData.home[1] + dLng] : null;
                finalPilot = routeData.pilot ? [routeData.pilot[0] + dLat, routeData.pilot[1] + dLng] : null;
                finalAnchor = targetStart; // El ancla se pone encima del dron
            }

            setSpotConfigs(prev => ({
                ...prev,
                [activeDrawingDevice]: {
                    waypoints: finalWaypoints,
                    home: finalHome,
                    pilot: finalPilot,
                    isPredefined: true,       
                    dragAnchor: finalAnchor   // En ambos modos permitimos arrastrar después de aplicarlo
                }
            }));
        }
    };

    // 👇 2. NUEVA FUNCIÓN PARA PREGUNTAR AL SERVIDOR
    const checkActiveSchedules = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${process.env.REACT_APP_API_URL}/schedules`, {
                headers: { 'x-access-token': token }
            });
            if (res.ok) {
                const data = await res.json();
                setHasActiveSchedules(data.length > 0); // Será true si hay 1 o más, false si hay 0
                
                // Opcional: Si el panel estaba abierto y se han borrado todas, lo cerramos
                if (data.length === 0 && showSchedulesPanel) {
                    setShowSchedulesPanel(false);
                }
            }
        } catch (e) {
            console.error("Error comprobando schedules:", e);
        }
    };

    const handleSpotSelect = (ident, centerCoords) => {
        setActiveDrawingDevice(ident);
        setSpotConfigs(prev => ({
            ...prev,
            [ident]: { waypoints: [], home: [centerCoords[0] + 0.002, centerCoords[1] + 0.019], pilot: [centerCoords[0] - 0.002, centerCoords[1] + 0.025] }
        }));
    };

    const handleSpotDeselect = (ident) => {
        if (activeDrawingDevice === ident) setActiveDrawingDevice(null);
        setSpotConfigs(prev => {
            const newConfigs = { ...prev };
            delete newConfigs[ident];
            return newConfigs;
        });
    };

    // Bloquea los clicks para crear waypoints en el mapa
    const handleSpotLock = (ident) => {
        if (activeDrawingDevice === ident) setActiveDrawingDevice(null);
    };

    const updateSpotMarker = (ident, type, latlng) => {
        setSpotConfigs(prev => ({
            ...prev,
            [ident]: { ...prev[ident], [type]: [latlng.lat, latlng.lng] }
        }));
    };

    const handleMapClick = (e) => {
        if (activeDrawingDevice) {
            const targetDevice = devices.find(d => d.ident === activeDrawingDevice);
            if (targetDevice && targetDevice.spoofing) return; 
            
            const { lat, lng } = e.latlng;
            setSpotConfigs(prev => {
                const currentConfig = prev[activeDrawingDevice] || { waypoints: [], home: null, pilot: null };
                if (currentConfig.isPredefined) return prev;
                return {
                    ...prev,
                    [activeDrawingDevice]: { ...currentConfig, waypoints: [...currentConfig.waypoints, [lat, lng]] }
                };
            });
        }
    };

    const fetchDevices = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/`, {
                method: 'GET',
                headers: { 'x-access-token': token, 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                setDevices(data);
            } else if (response.status === 401 || response.status === 403 || response.status === 500) {
                localStorage.removeItem('token');
                localStorage.removeItem('Login');
                window.location.href = '/'; 
            }
        } catch (error) {
            console.error("Error obteniendo detectores:", error);
        }
    };

    useEffect(() => {
        fetchDevices();
        const radarInterval = setInterval(() => {
            fetchDevices();
            checkActiveSchedules(); // <--- NUEVO: Comprobación cada 5 segundos
        }, 5000);
        const timer = setTimeout(() => { if (mapRef.current) mapRef.current.leafletElement.invalidateSize(); }, 200);
        return () => { clearTimeout(timer); clearInterval(radarInterval); };
    }, [showSchedulesPanel]);

    const handleZoomIn = () => { if (mapRef.current) mapRef.current.leafletElement.zoomIn(); };
    const handleZoomOut = () => { if (mapRef.current) mapRef.current.leafletElement.zoomOut(); };

    const handleDeviceSelect = (lat, lon) => {
        if (mapRef.current) mapRef.current.leafletElement.flyTo([lat, lon], 15, { animate: true, duration: 1.2 });
    };

    const currentUser = localStorage.getItem('Login') || "";
    const currentRole = localStorage.getItem('role'); 
    const isAdmin = currentUser.toLowerCase() === 'admin@deadergate' || currentRole === 'admin';

    // --- FUNCIÓN DE LOGOUT ---
    const handleLogout = () => {
        // Pedimos confirmación para evitar cierres accidentales en medio de una operación
        if (window.confirm("Are you sure you want to close the session?")) {
            localStorage.removeItem('token');
            localStorage.removeItem('Login');
            localStorage.removeItem('role');
            localStorage.removeItem('email');
            window.location.href = '/'; // Expulsa al usuario a la pantalla de login
        }
    };

    return (
        <div className="map-wrapper-fullscreen">
            <Map
                ref={mapRef}
                center={[41.728, 1.823]}
                zoom={13}
                style={{ height: "100vh", width: "100vw" }}
                zoomControl={false} 
                tap={false}
                onClick={handleMapClick} 
            >
                <TileLayer
                    url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                    attribution='&copy; CARTO'
                />

                {devices.map((device) => (
                    <DeviceMarker 
                        key={device.ident} 
                        device={device} 
                        onActionSuccess={fetchDevices} 
                        onSelect={handleDeviceSelect}
                        registerMarker={(ident, ref) => {
                            if (ref) markerRefs.current[ident] = ref;
                        }}
                        onSpotSelect={handleSpotSelect}
                        onSpotDeselect={handleSpotDeselect}
                        spotConfig={spotConfigs[device.ident]}
                        onSpotLock={handleSpotLock}
                        onArmedChange={(ident, status) => setArmedDevices(prev => ({...prev, [ident]: status}))}
                        globalCancelSignal={cancelSignal}
                    />
                ))}

                {Object.entries(spotConfigs).map(([ident, config]) => {
                    if (!config) return null;
                    const deviceData = devices.find(d => d.ident === ident);
                    const isSpoofing = deviceData ? deviceData.spoofing : false;
                    const isArmed = armedDevices[ident] || false;
                    const isLocked = isSpoofing || isArmed;

                    return (
                        <React.Fragment key={`spot-${ident}`}>
                            {config.waypoints.length > 0 && (
                                <Polyline positions={config.waypoints} color="#1a1a1a" weight={4} opacity={0.8} dashArray="10, 10" lineJoin="round" />
                            )}
                            {config.waypoints.map((wp, idx) => (
                                <CircleMarker key={idx} center={wp} radius={5} color="#1a1a1a" fillColor="#111" fillOpacity={1}>
                                    {idx === 0 && <Tooltip permanent direction="right" className="spot-tooltip">START</Tooltip>}
                                </CircleMarker>
                            ))}
                            {config.home && (
                                <Marker position={config.home} icon={homeIcon} draggable={!isLocked} zIndexOffset={1500} onDragend={(e) => updateSpotMarker(ident, 'home', e.target.getLatLng())} />
                            )}
                            {config.pilot && (
                                <Marker position={config.pilot} icon={pilotIcon} draggable={!isLocked} zIndexOffset={1500} onDragend={(e) => updateSpotMarker(ident, 'pilot', e.target.getLatLng())} />
                            )}

                            {/* 👇 NUEVO: EL ANCLA PARA ARRASTRAR LA RUTA COMO UN BLOQUE SÓLIDO 👇 */}
                            {config.isPredefined && !isLocked && config.dragAnchor && (
                                <Marker
                                    position={config.dragAnchor}
                                    icon={dragHandleIcon}
                                    draggable={true}
                                    zIndexOffset={2000}
                                    onDrag={(e) => {
                                        // Movimiento en tiempo real del bloque entero
                                        const newPos = e.target.getLatLng();
                                        setSpotConfigs(prev => {
                                            const current = prev[ident];
                                            const dLat = newPos.lat - current.dragAnchor[0];
                                            const dLng = newPos.lng - current.dragAnchor[1];

                                            return {
                                                ...prev,
                                                [ident]: {
                                                    ...current,
                                                    dragAnchor: [newPos.lat, newPos.lng],
                                                    waypoints: current.waypoints.map(wp => [wp[0] + dLat, wp[1] + dLng]),
                                                    home: current.home ? [current.home[0] + dLat, current.home[1] + dLng] : null,
                                                    pilot: current.pilot ? [current.pilot[0] + dLat, current.pilot[1] + dLng] : null
                                                }
                                            };
                                        });
                                    }}
                                >
                                </Marker>
                            )}
                        </React.Fragment>
                    );
                })}

                </Map>

                {/* 👇 BARRA DE HERRAMIENTAS DE DIBUJO SPOT (UNDO / SAVE / CLEAR) 👇 */}
            {activeDrawingDevice && spotConfigs[activeDrawingDevice]?.waypoints.length > 0 && !spotConfigs[activeDrawingDevice]?.isPredefined && (
                <div 
                    style={{ 
                        position: 'absolute', 
                        top: '25px', 
                        left: '50%', 
                        transform: 'translateX(-50%)', 
                        zIndex: 1000,
                        display: 'flex', // Convierte el contenedor en una fila
                        gap: '15px',     // Espacio entre botones
                        alignItems: 'center'
                    }}
                    onMouseDown={(e) => e.stopPropagation()} 
                    onTouchStart={(e) => e.stopPropagation()} 
                    onClick={(e) => e.stopPropagation()} 
                    onDoubleClick={(e) => e.stopPropagation()}
                >
                    {/* BOTÓN 1: DESHACER (UNDO) */}
                    <button 
                        className="btn-circle" 
                        style={{ borderColor: '#555', color: '#ccc', background: 'rgba(26,26,26,0.95)' }}
                        title="Undo last waypoint"
                        onClick={(e) => {
                            e.stopPropagation();
                            setSpotConfigs(prev => {
                                const current = prev[activeDrawingDevice];
                                if (!current || current.waypoints.length === 0) return prev;
                                return {
                                    ...prev,
                                    [activeDrawingDevice]: {
                                        ...current,
                                        // slice(0, -1) devuelve todo el array menos el último elemento
                                        waypoints: current.waypoints.slice(0, -1) 
                                    }
                                };
                            });
                        }}
                    >
                        <i className="fa-solid fa-rotate-left"></i>
                    </button>

                    {/* BOTÓN 2: GUARDAR (SAVE PATH) */}
                    <button 
                        className="btn-pill" 
                        style={{ borderColor: '#555', color: '#ccc', background: 'rgba(26,26,26,0.95)', width: 'auto', height: '50px', padding: '0 25px' }}
                        onClick={async (e) => {
                            e.stopPropagation(); 
                            const routeName = window.prompt("Enter a codename for this tactical route (e.g., ALPHA-1):");
                            if (!routeName) return;
                            
                            const token = localStorage.getItem('token');
                            const res = await fetch(`${process.env.REACT_APP_API_URL}/routes`, {
                                method: 'POST',
                                headers: { 'x-access-token': token, 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    name: routeName,
                                    waypoints: spotConfigs[activeDrawingDevice].waypoints,
                                    home: spotConfigs[activeDrawingDevice].home,
                                    pilot: spotConfigs[activeDrawingDevice].pilot
                                })
                            });
                            if (res.ok) {
                                alert("Route saved to Library successfully!");
                            } else {
                                alert("Error saving route. That codename might already exist.");
                            }
                        }}
                    >
                        <i className="fa-solid fa-floppy-disk" style={{marginRight: '8px'}}></i> SAVE PATH
                    </button>

                    {/* BOTÓN 3: BORRAR TODO (CLEAR) */}
                    <button 
                        className="btn-circle" 
                        style={{ borderColor: '#555', color: '#ccc', background: 'rgba(26,26,26,0.95)' }}
                        title="Clear path"
                        onClick={(e) => {
                            e.stopPropagation();
                            setSpotConfigs(prev => ({
                                ...prev,
                                [activeDrawingDevice]: {
                                    ...prev[activeDrawingDevice],
                                    waypoints: [] // Vaciamos el array de puntos
                                }
                            }));
                        }}
                    >
                        <i className="fa-solid fa-trash-can"></i>
                    </button>
                </div>
            )}

            <div className={`global-status-banner ${isAnySidePanelOpen ? 'panel-open' : ''}`}>
                <div className="status-grid">
                    <div className="status-row">
                        <span className="label">DEADER GATE</span>
                        <span className="value green">OK</span>
                    </div>
                    <div className="status-row">
                        <span className="label">DEVICES</span>
                        <span className="value">{onlineDevices} / {totalDevices}</span>
                    </div>
                    <div className="status-row alert-state">
                        <span className="label">SPOOFING</span>
                        <span className="value ${spoofingDevices > 0 ? 'red' : ''}">
                            {spoofingDevices}
                        </span>
                    </div>
                </div>
            </div>

            <div className="ui-left-container">
                <button className="btn-circle" onClick={handleZoomIn}><i className="fa-solid fa-plus"></i></button>
                <button className="btn-circle" onClick={handleZoomOut}><i className="fa-solid fa-minus"></i></button>
            </div>

            {/* BOTONES DERECHOS (NAVEGACIÓN / AJUSTES / LOGOUT) */}
            <div className={`ui-right-container ${isAnySidePanelOpen ? 'panel-open' : ''} ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
                
                {/* 👇 CONTENEDOR DE BOTONES (Horizontal en PC, Vertical oculto en Móvil) 👇 */}
                <div className="action-buttons-wrapper">
                    {hasActiveSchedules && (
                        <button 
                            className="btn-circle" 
                            title="Active Schedules" 
                            style={{ color: '#00bcd4', borderColor: '#00bcd4' }}
                            onClick={() => { setShowSchedulesPanel(true); setShowDirectoryPanel(false); setShowAlertsPanel(false); setShowSettingsPanel(false); setShowRouteLibraryPanel(false); setShowDetectorDetailPanel(false); setIsMobileMenuOpen(false); }}
                        >
                            <i className="fa-solid fa-stopwatch"></i>
                        </button>
                    )}

                    <button className="btn-circle" title="Assets Directory" onClick={() => { setShowDirectoryPanel(true); setShowAlertsPanel(false); setShowSettingsPanel(false); setShowRouteLibraryPanel(false); setShowDetectorDetailPanel(false); setIsMobileMenuOpen(false); }}>
                        <i className="fa-solid fa-satellite-dish"></i>
                    </button>
                    
                    <button className="btn-circle" title="System Alerts" onClick={() => { setShowAlertsPanel(true); setShowDirectoryPanel(false); setShowSettingsPanel(false); setShowRouteLibraryPanel(false); setShowDetectorDetailPanel(false); setIsMobileMenuOpen(false); }}>
                        <i className="fa-solid fa-triangle-exclamation"></i>
                    </button>

                    <button className="btn-circle" title="Route Library" onClick={() => { setShowRouteLibraryPanel(true); setShowSchedulesPanel(false); setShowDirectoryPanel(false); setShowAlertsPanel(false); setShowSettingsPanel(false); setShowDetectorDetailPanel(false); setIsMobileMenuOpen(false); }}>
                        <i className="fa-solid fa-map-location-dot"></i>
                    </button>
                    
                    {isAdmin && (
                        <button className="btn-circle" title="Server Settings" onClick={() => { setShowSettingsPanel(true); setShowDirectoryPanel(false); setShowAlertsPanel(false); setShowRouteLibraryPanel(false); setShowDetectorDetailPanel(false); setIsMobileMenuOpen(false); }}>
                            <i className="fa-solid fa-gear"></i>
                        </button>
                    )}

                    <button className="btn-circle btn-logout" title="Log Out" onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}>
                        <i className="fa-solid fa-right-from-bracket"></i>
                    </button>
                </div>

                {/* 👇 NUEVO: BOTÓN MAESTRO PARA MÓVILES (Solo se ve en pantallas pequeñas) 👇 */}
                <button 
                    className="btn-circle mobile-menu-toggle" 
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    style={{ backgroundColor: isMobileMenuOpen ? '#ff3333' : '#1a1a1a', borderColor: isMobileMenuOpen ? '#ff3333' : '#1a1a1a' }}
                >
                    <i className={`fa-solid ${isMobileMenuOpen ? 'fa-xmark' : 'fa-bars'}`}></i>
                </button>
            </div>

            <Alerts isOpen={showAlertsPanel} onClose={() => setShowAlertsPanel(false)} />
            <Schedules 
                isOpen={showSchedulesPanel} 
                onClose={() => setShowSchedulesPanel(false)}
                onScheduleCancelled={(ident) => {
                    setCancelSignal({ ident: ident, timestamp: Date.now() }); 
                    checkActiveSchedules(); // Forzamos que actualice el icono azul del cronómetro superior
                }} 
            />
            <RouteLibrary 
                isOpen={showRouteLibraryPanel} 
                onClose={() => setShowRouteLibraryPanel(false)}
                onApplyRoute={handleApplyRoute}
                activeDrawingDevice={activeDrawingDevice}
            />
            <DetectorsGeneral isOpen={showDirectoryPanel} onClose={() => setShowDirectoryPanel(false)} onSelectDetector={(ident) => {
                setShowDirectoryPanel(false); setSelectedDetectorId(ident); setShowDetectorDetailPanel(true);
                const targetDevice = devices.find(d => d.ident === ident); 
                if (targetDevice && targetDevice.location && targetDevice.location.lat) {
                    if (mapRef.current) mapRef.current.leafletElement.flyTo([targetDevice.location.lat, targetDevice.location.lon], 16, { animate: true, duration: 1.5 });
                }
            }}/>
            <Detector isOpen={showDetectorDetailPanel} onClose={() => setShowDetectorDetailPanel(false)} onBack={() => { setShowDetectorDetailPanel(false); setShowDirectoryPanel(true); }} detectorId={selectedDetectorId} onDetectorDeleted={() => { fetchDevices(); setShowDetectorDetailPanel(false); setShowDirectoryPanel(true); }}/>
            <ServerSettings isOpen={showSettingsPanel} onClose={() => setShowSettingsPanel(false)} />
        </div>
    );
}

export default MapView;